<template>
  <main class="w-full px-[14px] py-[20px] bg-white rounded-lg mt-[10px]">
    <div class="flow-root">
      <div class="min-w-0 w-full flex mb-5 items-center">
        <SearchBar
          placeholder="Cari Website"
          @input="onSearch($event)"
        />
        <MicrositeFilter
          v-if="isSuperAdmin"
          class="sm:ml-0 lg:ml-6"
          :params="params"
          :data="organization"
          @change:filter="onChangeFilter($event)"
        />
      </div>

      <section class="flex flex-col items-center justify-center w-full h-full">
        <JdsSpinner
          v-show="isLoading"
          size="56px"
          class="py-14"
        />
      </section>
      <section
        v-if="data.length === 0 && params.q && !isLoading"
        class="flex flex-col items-center justify-center w-full h-full py-6 gap-[10px]"
        data-cy="j-site-list__not-found"
      >
        <SearchNotFoundIcon />
        <h1 class="font-semibold font-inter text-gray-900 text-base">
          Website tidak ditemukan!
        </h1>
        <p class="text-gray-700 text-sm font-inter">
          Cobalah cek kembali nama website yang kamu inputkan apakah sudah sesuai
        </p>
      </section>
      <ul
        v-if="data.length > 0 && !isLoading"
        role="list"
        class="flex flex-col gap-[10px]"
      >
        <li
          v-for="(item, index) in data"
          :key="index"
          class="flex items-center gap-[10px]"
          data-cy="j-site-list"
        >
          <span class="text-blue-gray-900 font-medium">
            {{ `${index + 1}.` }}
          </span>
          <div class="flex items-center justify-between w-full px-4 h-[52px] border border-gray-300 rounded-xl overflow-hidden">
            <div class="flex items-center gap-4 mr-4 flex-1 min-w-0">
              <div class="w-[34px] h-[34px] overflow-hidden shrink-0">
                <img
                  :src="item.favicon || item.logo"
                  :alt="item.name"
                  class="w-full h-full object-cover"
                >
              </div>
              <div class="flex flex-col border-r border-gray-300 pr-4 min-w-0">
                <p
                  class="truncate text-sm font-roboto font-medium text-blue-gray-800"
                  data-cy="j-site-list__name"
                  :title="item.name"
                >
                  {{ item.name }}
                </p>
                <p
                  class="truncate text-xs font-roboto text-blue-gray-200"
                  data-cy="j-site-list__organization"
                >
                  {{ item.organization }}
                </p>
              </div>
              <div class="flex flex-col shrink-0 max-w-max pl-4">
                <p
                  class="text-xs text-blue-gray-300 whitespace-nowrap"
                  data-cy="j-site-list__domain"
                >
                  {{ `https://${item.domain}` }}
                </p>
                <p
                  data-cy="j-site-list__updated-at"
                  class="text-xs text-blue-gray-200"
                >
                  {{ formatDate(item.updated_at) }}
                </p>
              </div>
            </div>

            <div class="flex justify-center items-center shrink-0">
              <div
                v-if="item.status === 'APPROVED' && !item.is_active"
                data-cy="j-site-list__site-warning"
              >
                <WarningTriangleIcon v-if="warningMap[item.id]" />
              </div>

              <OptionDown
                v-if="isEnabledDeleteSite"
                :item="item"
                @manage="onManage(item.id)"
                @delete="toggleDeletePrompt(item.id)"
              />

              <BaseButton
                v-else
                class="text-green-700 font-bold text-sm border-none"
                data-cy="j-site-list__button-manage"
                @click="onManage(item.id)"
              >
                <template #icon-left>
                  <SettingIcon class="w-5 h-5 fill-green-800" />
                </template>
                <p>Kelola</p>
              </BaseButton>
            </div>
          </div>
        </li>
      </ul>
    </div>

    <!-- Delete Action Prompt -->
    <BaseModal
      :open="isDeletePromptOpen"
      data-cy="jsite__modal"
      @close="toggleDeletePrompt"
    >
      <div class="w-full h-full grid grid-cols-4 gap-4">
        <button
          v-if="!deleteLoading"
          class="absolute top-0 right-0 pr-3 pt-3"
          @click="toggleDeletePrompt"
        >
          <JdsIcon
            name="times"
            size="20px"
            fill="#6B7280"
          />
        </button>
        <div class="flex items-center justify-center">
          <div class="flex w-16 h-16 items-center justify-center bg-red-50 rounded-full">
            <JdsIcon
              name="trash"
              size="32px"
              fill="#F44336"
            />
          </div>
        </div>
        <div class="col-span-3 flex flex-col justify-center">
          <h1 class="font-roboto text-xl leading-8 font-medium text-gray-900 mb-2">
            Menghapus Website
          </h1>
          <p class="font-lato text-sm text-gray-600">
            Apakah anda yakin ingin menghapus Website ?
          </p>
        </div>
      </div>
      <template #footer>
        <div class="flex gap-4 justify-end">
          <BaseButton
            v-if="!deleteLoading"
            class="border-gray-300 hover:bg-gray-100 text-sm text-gray-600"
            data-cy="jsite__modal-button--cancel"
            @click="toggleDeletePrompt"
          >
            Batalkan
          </BaseButton>
          <BaseButton
            class="bg-green-700 hover:bg-green-600 text-sm font-normal text-white"
            :disabled="deleteLoading"
            data-cy="jsite__modal-button--delete"
            @click="onDeleteJsite"
          >
            <p v-if="!deleteLoading">
              Iya, saya yakin
            </p>
            <p
              v-else
              class="flex gap-2 items-center text-gray-500"
            >
              <JdsSpinner
                size="16"
                foreground="#757575"
              />
              Loading...
            </p>
          </BaseButton>
        </div>
      </template>
    </BaseModal>
  </main>
</template>

<script>
import { mapGetters } from 'vuex';
import { formatDate } from '@/common/helpers/date';
import BaseButton from '@/common/components/BaseButton';
import BaseModal from '@/common/components/BaseModal';
import MicrositeFilter from '@/components/Microsite/Filter';
import SearchBar from '@/common/components/SearchBar';
import SearchNotFoundIcon from '@/assets/icons/search-not-found.svg?inline';
import WarningTriangleIcon from '@/assets/icons/warning-triangle.svg?inline';
import { RepositoryFactory } from '@/repositories/microsite/RepositoryFactory';
import OptionDown from '@/components/Microsite/List/OptionDown.vue';
import SettingIcon from '@/assets/icons/setting.svg?inline';

const settingsRepository = RepositoryFactory.get('settings');

export default {
  name: 'MicrositeList',
  components: {
    BaseButton,
    BaseModal,
    MicrositeFilter,
    OptionDown,
    SearchBar,
    SearchNotFoundIcon,
    WarningTriangleIcon,
    SettingIcon,
  },
  data() {
    return {
      formatDate,
      data: [],
      organization: [],
      params: {
        q: '',
      },
      isLoading: false,
      warningMap: {},
      isDeletePromptOpen: false,
      deleteLoading: false,
      idDeleteSite: '',
    };
  },
  computed: {
    ...mapGetters('auth', ['user']),
    isSuperAdmin() {
      return this.user?.role?.name === 'Super Admin';
    },
    isEnabledDeleteSite() {
      return this.$unleash.isEnabled('J-SITE-DELETE-SITE');
    },
  },
  async mounted() {
    await this.fetchSites();
    if (this.isSuperAdmin) {
      await this.fetchOrganization();
    }
  },
  methods: {
    async checkDomainLink(domain) {
      try {
        const res = await settingsRepository.checkDomain(
          { url: `https://${domain}` },
        );
        const data = res.data?.data;
        return data.is_available === false;
      } catch (e) {
        return true;
      }
    },

    async fetchSites() {
      this.isLoading = true;
      try {
        const response = await settingsRepository.getSites(this.params);
        const { data } = response.data;
        this.data = data;

        const warningPromises = (data || []).map(async (item) => {
          if (item.status === 'APPROVED' && !item.is_active) {
            const shouldWarn = await this.checkDomainLink(item.domain);
            this.$set(this.warningMap, item.id, shouldWarn);
          }
        });

        await Promise.all(warningPromises);
      } catch (error) {
        this.$toast({
          type: 'error',
          message: 'Gagal mendapatkan data list site, silakan coba beberapa saat lagi',
        });
      }
      this.isLoading = false;
    },

    async onManage(siteId) {
      try {
        const response = await this.$store.dispatch('microsite/generateJSiteUrl', siteId);
        const { url } = response.data || {};

        if (url) {
          window.location.href = url;
        }
      } catch (error) {
        // Token Expired
        if (error.response.status === 400) {
          try {
            await this.$store.dispatch('microsite/refreshJSiteToken');

            this.onManage(siteId);
          } catch (err) {
            this.$toast({
              type: 'error',
              message: 'Mohon maaf, terjadi kesalahan. Mohon coba beberapa saat lagi.',
            });

            console.error(err);
          }
        } else {
          this.$toast({
            type: 'error',
            message: 'Gagal membuka halaman kelola J-Site Anda. Mohon coba beberapa saat lagi.',
          });
        }
      }
    },

    setParams(data) {
      const newParams = { ...this.params, ...data };
      this.params = { ...newParams };
    },

    onSearch(query) {
      this.setParams({ q: query });
      this.fetchSites();
    },

    async fetchOrganization() {
      try {
        const response = await settingsRepository.getOrganization();
        const { data } = response.data;

        this.organization = (data && Array.isArray(data) && data.length > 0)
          ? data.map((item) => ({
            count: item.count,
            name: item.organization,
          }))
          : [];
      } catch (error) {
        this.$toast({
          type: 'error',
          message: 'Gagal mendapatkan data list organisasi, silakan coba beberapa saat lagi',
        });
      }
    },

    /**
     * Set new params when filter changes
     * and fetch events again
     *
     * @property {Array} organization - list of organization
     */
    onChangeFilter(data) {
      this.setParams(data);
      this.fetchSites();
    },

    toggleDeletePrompt(idSite = '') {
      this.deleteLoading = false;
      this.isDeletePromptOpen = !this.isDeletePromptOpen;
      this.idDeleteSite = idSite;
    },
    async onDeleteJsite() {
      this.deleteLoading = true;

      try {
        await settingsRepository.deleteSite(this.idDeleteSite);
        await this.fetchSites();

        this.$toast({
          type: 'success',
          message: 'Website berhasil dihapus',
        });
      } catch (error) {
        this.$toast({
          type: 'error',
          message: 'Gagal menghapus website, silakan coba beberapa saat lagi',
        });
      } finally {
        this.deleteLoading = false;
        this.isDeletePromptOpen = false;
      }
    },
  },
};
</script>
